/* eslint-disable no-use-before-define */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { ServerContext } from '@bingads-webui-campaign-react/shared-app-capi';
import { CcJsDataContextProvider } from '@bingads-webui-cc-react/js-data-rest';
import { UrlBuilderContextProvider } from '@bingads-webui-cc-react/url-builder-react';
import useScope from './use-scope';

/**
 * Adds the Client Center API context to the specified component.
 * @param {*} Component The component requiring the API context.
 * @returns {Element} The new component, operating within the Client Center API context.
 */
export function withClientCenterApiContext(Component) {
  const HoC = (props) => {
    const scope = useScope();
    return (
      <ClientCenterApiContext {...scope}>
        <Component {...props} />
      </ClientCenterApiContext>
    );
  };

  HoC.displayName = `WithClientCenterApiContext(${getDisplayName(Component)})`;
  hoistNonReactStatics(HoC, Component);

  return HoC;
}

/**
 * Defines a context provider for Client Center API calls.
 * @param {*} props The scope of the components that will be making the API calls.
 * @param {number} props.aid The account ID for the API calls made within this context.
 * @param {number} props.cid The customer ID for the API calls made within this context.
 * @param {number} props.uid The user ID for the API calls made within this context.
 * @returns {object} A composite context provider component for Client Center API calls.
 */
function ClientCenterApiContext({
  aid,
  cid,
  uid,
  children,
}) {
  const [{
    ServicesEndpoints: {
      ClientCenterUIAPI: ccApi,
    },
  }] = useContext(ServerContext);

  return (
    <CcJsDataContextProvider customerId={cid} userId={uid} clientCenterBaseUrl={ccApi}>
      <UrlBuilderContextProvider aid={aid} cid={cid} uid={uid} ClientCenterBaseUrl={ccApi}>
        {children}
      </UrlBuilderContextProvider>
    </CcJsDataContextProvider>
  );
}

ClientCenterApiContext.propTypes = {
  uid: PropTypes.number.isRequired,
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
};
